import React, { useState, useRef } from 'react';

export interface Point {
  x: number;
  y: number;
}

export interface WindowCalibrationOverlayProps {
  imageUrl: string;
  initialCalibration?: {
    topLeft: Point;
    topRight: Point;
    bottomLeft: Point;
    bottomRight: Point;
  };
  onConfirm: (data: {
    boundingBox: { x: number; y: number; width: number; height: number };
    points: { topLeft: Point; topRight: Point; bottomLeft: Point; bottomRight: Point };
    tiltAngle: number;
    aspect: number;
  }) => void;
}

type Corner = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

export const WindowCalibrationOverlay: React.FC<WindowCalibrationOverlayProps> = ({
  imageUrl,
  initialCalibration,
  onConfirm,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [points, setPoints] = useState(
    initialCalibration || {
      topLeft: { x: 0.1, y: 0.1 },
      topRight: { x: 0.9, y: 0.1 },
      bottomLeft: { x: 0.1, y: 0.9 },
      bottomRight: { x: 0.9, y: 0.9 },
    }
  );

  const [activeCorner, setActiveCorner] = useState<Corner | null>(null);
  const [dim, setDim] = useState({ width: 800, height: 600 });

  React.useEffect(() => {
    if (containerRef.current) {
      setDim({
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight
      });
    }
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setDim({
          width: entry.contentRect.width,
          height: entry.contentRect.height
        });
      }
    });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const handlePointerDown = (e: React.PointerEvent, corner: Corner) => {
    e.stopPropagation();
    setActiveCorner(corner);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!activeCorner || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    
    // Add touch offset for mobile (e.g., -40px on Y axis so finger doesn't block pin)
    const isTouch = e.pointerType === 'touch';
    const offsetY = isTouch ? 40 : 0;

    let newX = e.clientX - rect.left;
    let newY = e.clientY - rect.top - offsetY;

    // Constrain to image bounds
    newX = Math.max(0, Math.min(newX, rect.width));
    newY = Math.max(0, Math.min(newY, rect.height));

    setPoints((prev) => ({
      ...prev,
      [activeCorner]: { x: newX / rect.width, y: newY / rect.height },
    }));
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (activeCorner) {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      setActiveCorner(null);
    }
  };

  const handleReset = () => {
    if (initialCalibration) {
      setPoints(initialCalibration);
    } else {
      setPoints({
        topLeft: { x: 0.1, y: 0.1 },
        topRight: { x: 0.9, y: 0.1 },
        bottomLeft: { x: 0.1, y: 0.9 },
        bottomRight: { x: 0.9, y: 0.9 },
      });
    }
  };
  
  const handleAutoFit = () => {
    setPoints({
      topLeft: { x: 0.1, y: 0.1 },
      topRight: { x: 0.9, y: 0.1 },
      bottomLeft: { x: 0.1, y: 0.9 },
      bottomRight: { x: 0.9, y: 0.9 },
    });
  };

  const getPixelPoint = (pt: Point) => {
    // If somehow it's already in pixels (e.g. legacy sampleRooms), don't multiply
    const x = pt.x > 1 ? pt.x : pt.x * dim.width;
    const y = pt.y > 1 ? pt.y : pt.y * dim.height;
    return { x, y };
  };

  const handleConfirm = () => {
    const pxPoints = {
      topLeft: getPixelPoint(points.topLeft),
      topRight: getPixelPoint(points.topRight),
      bottomLeft: getPixelPoint(points.bottomLeft),
      bottomRight: getPixelPoint(points.bottomRight),
    };

    const minX = Math.min(pxPoints.topLeft.x, pxPoints.bottomLeft.x);
    const maxX = Math.max(pxPoints.topRight.x, pxPoints.bottomRight.x);
    const minY = Math.min(pxPoints.topLeft.y, pxPoints.topRight.y);
    const maxY = Math.max(pxPoints.bottomLeft.y, pxPoints.bottomRight.y);

    const width = maxX - minX;
    const height = maxY - minY;

    const dx = pxPoints.topRight.x - pxPoints.topLeft.x;
    const dy = pxPoints.topRight.y - pxPoints.topLeft.y;
    const tiltAngle = Math.atan2(dy, dx) * (180 / Math.PI);
    const aspect = width / height;

    onConfirm({
      boundingBox: { x: minX, y: minY, width, height },
      points: pxPoints,
      tiltAngle,
      aspect,
    });
  };

  const pTL = getPixelPoint(points.topLeft);
  const pTR = getPixelPoint(points.topRight);
  const pBL = getPixelPoint(points.bottomLeft);
  const pBR = getPixelPoint(points.bottomRight);

  const polygonPath = `${pTL.x},${pTL.y} ${pTR.x},${pTR.y} ${pBR.x},${pBR.y} ${pBL.x},${pBL.y}`;

  const midTop = { x: (pTL.x + pTR.x)/2, y: (pTL.y + pTR.y)/2 };
  const midBottom = { x: (pBL.x + pBR.x)/2, y: (pBL.y + pBR.y)/2 };
  const midLeft = { x: (pTL.x + pBL.x)/2, y: (pTL.y + pBL.y)/2 };
  const midRight = { x: (pTR.x + pBR.x)/2, y: (pTR.y + pBR.y)/2 };

  return (
    <div className="flex flex-col items-center gap-4 w-full max-w-3xl mx-auto p-4 bg-white rounded-xl shadow-lg border border-gray-100">
      <div
        ref={containerRef}
        className="relative w-full h-[600px] bg-gray-100 overflow-hidden select-none touch-none rounded-lg"
        style={{ touchAction: 'none' }}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        <img
          src={imageUrl}
          alt="Room Workspace"
          className="absolute top-0 left-0 w-full h-full object-contain pointer-events-none"
        />

        <svg className="absolute top-0 left-0 w-full h-full pointer-events-none">
          <polygon
            points={polygonPath}
            fill="rgba(6, 182, 212, 0.3)" /* cyan-500 with opacity */
            stroke="rgba(245, 158, 11, 0.8)" /* amber-500 */
            strokeWidth="2"
            strokeDasharray="5,5"
          />
          {/* Internal Grid Lines */}
          <line x1={midTop.x} y1={midTop.y} x2={midBottom.x} y2={midBottom.y} stroke="rgba(245,158,11,0.5)" strokeWidth="1" strokeDasharray="3,3" />
          <line x1={midLeft.x} y1={midLeft.y} x2={midRight.x} y2={midRight.y} stroke="rgba(245,158,11,0.5)" strokeWidth="1" strokeDasharray="3,3" />
        </svg>

        {Object.entries(points).map(([key, point]) => {
          const p = getPixelPoint(point as Point);
          return (
            <div
              key={key}
              className="absolute w-10 h-10 -ml-5 -mt-5 bg-white border-4 border-amber-500 rounded-full shadow-lg cursor-grab active:cursor-grabbing flex items-center justify-center pointer-events-auto transition-transform hover:scale-110"
              style={{ left: p.x, top: p.y }}
              onPointerDown={(e) => handlePointerDown(e, key as Corner)}
            >
              <div className="w-3 h-3 bg-amber-500 rounded-full" />
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap justify-center gap-3 mt-2 w-full">
        <button
          onClick={handleReset}
          className="flex-1 min-w-[120px] px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg shadow-sm hover:bg-gray-200 transition-colors border border-gray-200"
        >
          Reset Pins
        </button>
        <button
          onClick={handleAutoFit}
          className="flex-1 min-w-[120px] px-4 py-2 bg-indigo-50 text-indigo-700 font-medium rounded-lg shadow-sm hover:bg-indigo-100 transition-colors border border-indigo-200"
        >
          Auto-Fit Window
        </button>
        <button
          onClick={handleConfirm}
          className="flex-1 min-w-[160px] px-6 py-2 bg-blue-600 text-white font-medium rounded-lg shadow-sm hover:bg-blue-700 transition-colors"
        >
          Confirm Calibration
        </button>
      </div>
    </div>
  );
};
